import React, { useState, useEffect, useRef } from 'react'
import { shallow } from 'zustand/shallow'
import * as d3 from 'd3'
import { toast } from 'react-toastify'
import { useTrackTransactionStatus } from '@multiversx/sdk-dapp/hooks/transactions'
import { useDisconnect, useAccount } from 'wagmi'

// @mui material components
import TextField from '@mui/material/TextField'
import { useParams, useNavigate } from 'react-router-dom'

import { useChainAndNetwork } from 'src/store/chainAndNetwork'
// Data
import ConfirmExecuteModal from './components/ConfirmExecuteModal/ConfirmExecuteModal'
import CreateModal from './components/CreateModal/CreateModal'
import ConfirmRemoveModal from './components/ConfirmRemoveModal/ConfirmRemoveModal'
import UpdateModal from './components/UpdateModal/UpdateModal'
import ReviewCsvModal from './components/ReviewCsvModal/ReviewCsvModal'
import SignerOptionsModal from './components/SignerOptionsModal'
import ExternalSignerOptionsModal from './components/ExternalSignerOptionsModal'
import MnemonicModal from './components/MnemonicModal/MnemonicModal'
import KeystoreModal from './components/KeystoreModal'
import PemModal from './components/PemModal'
import PrivateKeyModal from './components/PrivateKeyModal'
import AddTokenModal from '../../components/AddTokenModal'
import TransactionsTable from './components/TransactionsTable/TransactionsTable'
import FormTitle from '../../components/kit/FormTitle'
import SectionTitle from '../../components/kit/SectionTitle'
import ChainOptions from '../../components/ChainOptions'
import NetworkOptions from '../../components/NetworkOptions'
import TokenSelect from '../../components/TokenSelect'
import { useAirdropState, useAirdropDispatch } from '../../contexts/airDropContext'

import {
  sendBulkTransactions,
  storeSigner,
  storeInitiatorAddress,
  addMultipleDetailedTransactions,
  getAllBulkById,
  setExternalWallet,
  getUserTokens,
  getMvxTokenDetails,
  updateTransactions,
} from '../../contexts/airdropActionCreators'
import {
  getMvxBalance,
  getEvmBalance,
  getEvmTokenBalance,
  getEsdtBalance,
  getEvmTokenDecimals,
} from './actions/blockchainActions'
import { checkMvxAddress, checkEthAddress } from '../../util/fieldValidations'
import { chainsDataType, networkType, networksData } from '../../util/chainsData'
import { tokenSelectObjectType } from '../../util/types'
import { formatNumber4Decimals } from '../../util/formats'
import { useRpcCountStore } from 'src/store/rpcCount'
import Button from 'src/ui/atoms/Button'
import './AirDrop.scss'

export default function Transactions() {
  const { chain, network, setChain } = useChainAndNetwork(
    (state) => ({
      chain: state.chain,
      network: state.network,
      setChain: state.setChain,
    }),
    shallow,
  )
  const [csvFileName, setCsvFileName] = useState('')
  const [csvFileClone, setCsvFileClone] = useState<any>(null)
  const [selectedTransactions, setSelectedTransactions] = useState<any[]>([])
  const [airdropData, setAirdropData] = useState<{
    chain: any
    transactions: any
    network: any
    tokenIdentifier: string
    tokenName: string
  }>({
    chain: '',
    transactions: [],
    network: '',
    tokenIdentifier: '',
    tokenName: '',
  })
  const [selectedRow, setSelectedRow] = useState(null)
  const [nativeCurrencyBalance, setNativeBalance] = useState(0)
  const [erroredEntries, setErroredEntries] = useState<any[]>([])
  const [userTokens, setUserTokens] = useState<tokenSelectObjectType[]>([])
  const [selectedToken, setSelectedToken] = useState<tokenSelectObjectType | null>(
    userTokens[0] || null,
  )
  const [selectedTokenbalance, setSelectedTokenBalance] = useState<any>(0)

  // Modals
  const [showExecuteModal, setShowExecuteModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [showSignerOptionsModal, setShowSignerOptionsModal] = useState(false)
  const [showExternalSignerOptionsModal, setShowExternalSignerOptionsModal] = useState(false)
  const [showMnemonicModal, setShowMnemonicModal] = useState(false)
  const [showPemModal, setShowPemModal] = useState(false)
  const [showKeystoreModal, setShowKeystoreModal] = useState(false)
  const [showReviewCsvModal, setShowReviewCsvModal] = useState(false)
  const [showPrivateKeyModal, setShowPrivateKeyModal] = useState(false)
  const [showAddTokenModal, setShowAddTokenModal] = useState(false)

  const [airdropTransactions, setAirdropTransactions] = useState<any[]>([])

  const csvInputRef = useRef<any>(0)
  const prevAuthStateRef = useRef<any>(0)

  const dispatchAirdropAction = useAirdropDispatch()
  const airdropState = useAirdropState()
  const { id } = useParams()
  const navigate = useNavigate()
  const { rpcCount, incrementRpcCount } = useRpcCountStore(
    (state) => ({ rpcCount: state.count, incrementRpcCount: state.increment }),
    shallow,
  )

  const handleGetBalance = async () => {
    let balance: any = 0

    if (
      (airdropData?.chain && airdropData?.chain === 'multiversx') ||
      (!airdropData?.chain && chain === 'multiversx')
    ) {
      balance = await getMvxBalance(airdropState.initiator)
    } else {
      balance = await getEvmBalance(
        airdropState.initiator,
        airdropData?.chain || chain,
        airdropData?.network || network,
        rpcCount,
        incrementRpcCount,
      )
    }
    setNativeBalance(balance)
  }

  const handleGetUserTokens = async () => {
    const tokens = await getUserTokens(chain, airdropState.initiator, network)
    tokens.push({
      name: networksData?.[chain as keyof chainsDataType]?.[network as networkType]?.nativeCurrency,
      tokenIdentifier: '',
      id: '',
      decimals: 18,
      operation: 'token',
      chain,
      network,
    })
    setUserTokens(tokens)
  }

  const handleChangeToken = async (token: string) => {
    if (!token) {
      return
    }

    const selectedTokenObject = userTokens.find((userToken) => userToken.name === token)
    if (selectedTokenObject) {
      setSelectedToken(selectedTokenObject)
    }
  }

  const handleGetEvmTokenBalance = async () => {
    if (!selectedToken) {
      return
    }

    let balance
    const newChain = airdropData?.chain.toLowerCase() || chain
    const newNetwork = airdropData?.network || network
    if (
      selectedToken?.name ===
      networksData[newChain as keyof chainsDataType][newNetwork as networkType].nativeCurrency
    ) {
      balance = await getEvmBalance(
        airdropState.initiator,
        newChain as keyof chainsDataType,
        newNetwork as networkType,
        rpcCount,
        incrementRpcCount,
      )
    } else {
      balance = await getEvmTokenBalance(
        selectedToken.tokenIdentifier,
        airdropState?.initiator,
        newChain as keyof chainsDataType,
        newNetwork as networkType,
        rpcCount,
        incrementRpcCount,
      )
    }

    setSelectedTokenBalance(balance)
  }

  const handleGetEsdtBalance = async () => {
    if (!selectedToken) {
      return
    }
    let balance

    const newNetwork = airdropData?.network || network
    if (selectedToken.name === networksData.multiversx[newNetwork as networkType]?.nativeCurrency) {
      balance = nativeCurrencyBalance
    } else {
      balance = await getEsdtBalance(airdropState.initiator, selectedToken.tokenIdentifier)
    }
    setSelectedTokenBalance(balance)
  }

  const handleGetAirdropTransactions = async () => {
    if (id) {
      const res = await getAllBulkById(id)
      setAirdropData(res)
      setAirdropTransactions(res.transactions)
      return res
    }
  }

  useEffect(() => {
    if (id) {
      const getAirdropData = async () => {
        const res = await handleGetAirdropTransactions()
        const tokenDecimals = res.tokenIdentifier
          ? res.chain === 'multiversx'
            ? await getMvxTokenDetails(res.tokenIdentifier)
            : await getEvmTokenDecimals(
                res.tokenIdentifier,
                res.chain,
                res.network,
                rpcCount,
                incrementRpcCount,
              )
          : 18
        // @ts-ignore
        setSelectedToken({
          tokenIdentifier: res.tokenIdentifier,
          name: res.tokenName,
          decimals: tokenDecimals,
        })

        setChain(res.chain)
      }

      getAirdropData()
    }
  }, [id])

  //Unset selected row on update modal close
  useEffect(() => {
    if (!showUpdateModal) {
      setSelectedRow(null)
    }
  }, [showUpdateModal])

  //Get user native token balance on initiator address change
  useEffect(() => {
    // if (airdropData?.network && airdropData?.network !== chain && airdropState.initiator) {
    //   console.log('initiator should clear')
    //   storeInitiatorAddress(dispatchAirdropAction, '');
    // }
    if (airdropState.initiator) {
      handleGetBalance()
    } else {
      setExternalWallet(dispatchAirdropAction, false)
    }
  }, [airdropState.initiator, chain, airdropData?.chain, showExecuteModal])

  //Get user tokens on airdrop data set
  useEffect(() => {
    if (chain && airdropState.initiator && !id) {
      handleGetUserTokens()
    }
  }, [airdropState.initiator, chain, id])

  //Preselect a user token after the above fetch
  useEffect(() => {
    if (userTokens?.length > 0) {
      setSelectedToken(userTokens[0])
    }
  }, [userTokens])

  //Get specific evm token balance on token select
  useEffect(() => {
    if (selectedToken && airdropState.initiator) {
      if (
        (airdropData?.chain && airdropData?.chain === 'multiversx') ||
        (!airdropData?.chain && chain?.toLowerCase() === 'multiversx')
      ) {
        handleGetEsdtBalance()
      } else {
        handleGetEvmTokenBalance()
      }
    }
  }, [selectedToken, airdropState.initiator, airdropData?.chain])

  useEffect(() => {
    prevAuthStateRef.current = chain
  }, [chain])

  const prevAuthStateChain = prevAuthStateRef.current
  const isChainEvm = networksData[chain?.toLowerCase() as keyof chainsDataType]?.chainType === 'evm'
  const isChainMvx = networksData[chain?.toLowerCase() as keyof chainsDataType]?.chainType === 'mvx'
  const isInitiatorEvm = airdropState.initiator?.startsWith('0x')
  const isInitiatorMvx = airdropState.initiator?.startsWith('erd')

  //Clear wallet info if chain is switched between evm and mvx
  useEffect(() => {
    if (
      !id &&
      airdropState.initiator &&
      prevAuthStateChain &&
      prevAuthStateChain !== chain &&
      ((isChainEvm && isInitiatorMvx) || (isChainMvx && isInitiatorEvm))
    ) {
      handleClearWalletInfo()
    }
  }, [chain, prevAuthStateChain, airdropState.initiator, id])

  const handleSelectionChange = (selectionModel: any[]) => {
    setSelectedTransactions(selectionModel)
  }

  const toggleExecuteModal = async () => {
    setShowExecuteModal(!showExecuteModal)
  }

  const toggleCreateModal = () => {
    if (!showCreateModal && airdropTransactions.length === 100) {
      return toast.error('The airdrop has a limit of 100 transactions.', {
        progressClassName: 'simpleBar',
      })
    }

    setShowCreateModal(!showCreateModal)
  }

  const toggleRemoveModal = () => {
    setShowRemoveModal(!showRemoveModal)
  }

  const toggleUpdateModal = () => {
    setShowUpdateModal(!showUpdateModal)
  }

  const toggleReviewCsvModal = () => {
    setShowReviewCsvModal(!showReviewCsvModal)
  }

  const toggleSignerOptionsModal = () => {
    setShowSignerOptionsModal(!showSignerOptionsModal)
  }

  const toggleExternalSignerOptionsModal = () => {
    setShowExternalSignerOptionsModal(!showExternalSignerOptionsModal)
  }

  const toggleMnemonicModal = () => {
    setShowMnemonicModal(!showMnemonicModal)
  }

  const togglePemModal = () => {
    setShowPemModal(!showPemModal)
  }

  const toggleKeystoreModal = () => {
    setShowKeystoreModal(!showKeystoreModal)
  }

  const togglePrivateKeyModal = () => {
    setShowPrivateKeyModal(!showPrivateKeyModal)
  }

  const toggleAddTokenModal = () => {
    setShowAddTokenModal(!showAddTokenModal)
  }

  const handleTransactionClick = (params: any) => {
    if (params.row.status === 'new') {
      setSelectedRow(params.row)
      toggleUpdateModal()
    } else {
      toast.error('The transaction can no longer be modified.', {
        progressClassName: 'simpleBar',
      })
    }
  }

  const filteredTransactionsData = airdropTransactions?.filter(
    (tx) => selectedTransactions?.includes(tx?.id),
  )
  const validTransactions = filteredTransactionsData?.filter(
    (tx) => tx.status === 'new' || tx.status === 'failed',
  )

  const downloadCsvTemplate = () => {
    const a = document.createElement('a')
    const chainType =
      networksData[
        id
          ? (airdropData.chain.toLowerCase() as keyof chainsDataType)
          : (chain as keyof chainsDataType)
      ].chainType
    a.href = `${process.env.PUBLIC_URL}/templates/airdrop_template_${chainType}.csv`
    a.target = '_blank'
    a.setAttribute('download', `airdrop_template_${chainType}.csv`)
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const handleFileChange = (e: any) => {
    if (!e.target.files.length && csvFileClone) {
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(csvFileClone)

      e.target.files = dataTransfer.files
    }
    setCsvFileName(e.target.files[0]?.name)
  }

  const handleInputClick = (e: any) => {
    if (e.target.files.length) {
      setCsvFileClone(e.target.files[0])
    }
  }

  const handleCsvSubmit = (file: Blob) => {
    const reader = new FileReader()
    let data: d3.DSVRowArray<string>
    reader.onload = (e) => {
      if (e.target) {
        const text = e.target.result
        if (text) {
          data = d3.csvParse(text as string)
        }
      }

      if (data && data.length > 100) {
        return toast.error('The airdrop has a limit of 100 transactions.', {
          progressClassName: 'simpleBar',
        })
      }

      if (data && data.length + airdropTransactions.length > 100) {
        return toast.error('Adding these transactions will exceed the airdrop limit of 100.', {
          progressClassName: 'simpleBar',
        })
      }

      let erroredEntries: {
        row: number
        receiver?: string
        amount?: string | number
      }[] = []
      const parsedData = data.map((item, index) => {
        const values = item[data?.columns[0]]
        let parsedValues: string[] = []
        if (values) {
          parsedValues = values.split(';')
        }

        //Check if receiver is valid bech32 or eth address

        const isChainMvx =
          (airdropData?.chain && airdropData?.chain === 'multiversx') ||
          (!airdropData?.chain && chain === 'multiversx')
        const isChainNotMvx =
          (airdropData?.chain && airdropData?.chain !== 'multiversx') ||
          (!airdropData?.chain && chain !== 'multiversx')

        if (
          (isChainMvx && !checkMvxAddress(parsedValues[0]?.trim())) ||
          (isChainNotMvx && !checkEthAddress(parsedValues[0]?.trim()))
        ) {
          erroredEntries.push({
            row: index + 1,
            receiver: parsedValues[0]?.trim(),
          })
        }

        if (isNaN(parseFloat(parsedValues[1])) || parseFloat(parsedValues[1]) <= 0) {
          erroredEntries.push({
            row: index + 1,
            amount: parsedValues[1],
          })
        }

        return {
          receiver: parsedValues[0],
          amount: parseFloat(parsedValues[1]),
          sender: airdropState.initiator,
          status: 'new',
          network,
        }
      })
      if (erroredEntries.length > 0) {
        setErroredEntries(erroredEntries)
        toggleReviewCsvModal()
        return
      }
      handleSendTransactions(parsedData)
    }

    reader.readAsText(file)
    handleClearCsv()
  }

  const handleClearCsv = () => {
    setCsvFileName('')
    csvInputRef.current.value = ''
  }

  const handleClearCsvClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    handleClearCsv()
  }

  const handleSendTransactions = async (parsedData: any) => {
    if (id) {
      const res = await addMultipleDetailedTransactions(parsedData, id, airdropData.chain)
      setAirdropTransactions(res.transactions)
    } else {
      const res = await sendBulkTransactions(
        parsedData,
        airdropState.initiator,
        'token',
        network,
        chain,
        selectedToken as tokenSelectObjectType,
      )
      if (res.id) {
        navigate(`/airdrop/${res.id}`)
      }
    }
  }

  const handleFormSubmit = (e: any) => {
    e.preventDefault()
    const csvFile = csvInputRef.current?.files?.[0]
    if (!csvFile) {
      return
    }
    if (csvFile?.type !== 'text/csv') {
      return toast.error('The selected file is not a csv.')
    }

    handleCsvSubmit(csvFile)
  }

  const { isConnected: isWalletConnected } = useAccount({})
  const { disconnect } = useDisconnect({})

  const handleClearWalletInfo = () => {
    storeSigner(dispatchAirdropAction, null)
    setExternalWallet(dispatchAirdropAction, false)
    storeInitiatorAddress(dispatchAirdropAction, '')
    setUserTokens([])
    setNativeBalance(0)
    setSelectedTokenBalance(0)
    if (isWalletConnected) {
      disconnect()
    }
  }

  const sessionPayload = sessionStorage.getItem('airdropMvxRes')
  const sessionPayloadParsed = JSON.parse(sessionPayload || '{}')

  const transactionStatus = useTrackTransactionStatus({
    transactionId: sessionPayloadParsed?.sessionId,
    onSuccess: async () => {
      if (transactionStatus.transactions?.length) {
        const pendingTransactions = airdropTransactions?.filter((airdropTx) => {
          //@ts-ignore
          const matchingTxStatusItem = transactionStatus.transactions.find(
            (tx) => tx.hash === airdropTx.tx,
          )

          if (matchingTxStatusItem) {
            airdropTx.status = matchingTxStatusItem.status === 'success' ? 'completed' : 'failed'
            return true
          } else {
            return false
          }
        })
        await updateTransactions(pendingTransactions)
        await handleGetAirdropTransactions()
      }
    },
    onFail(transactionId, errorMessage) {
      console.error('fail', errorMessage)
    },
  })

  useEffect(() => {
    const updateMvxTransactions = async () => {
      if (transactionStatus?.isPending && sessionPayloadParsed.transactions) {
        transactionStatus?.transactions?.forEach((tx, index) => {
          sessionPayloadParsed.transactions[index].tx = tx?.hash
        })
        await updateTransactions(sessionPayloadParsed.transactions)
        await handleGetAirdropTransactions()
      }
    }

    updateMvxTransactions()
  }, [transactionStatus?.isPending])

  const renderConnectWalletSection = () => {
    return (
      <div>
        <div className="mb-4 flex">
          <SectionTitle title={`${id ? '1' : '2'}.  Choose transaction signing method`} />
        </div>
        {airdropState.initiator ? (
          <div className="walletInfoSection">
            <span className="walletAddress">Connected wallet: {airdropState.initiator}</span>
            <Button
              name="Clear wallet info"
              size="M"
              onClick={handleClearWalletInfo}
              type="secondary"
            />
          </div>
        ) : (
          <div className="align-center flex">
            <Button
              name="Local wallet"
              size="M"
              onClick={toggleSignerOptionsModal}
              type="primary"
            />
            <span className="mx-2">or</span>
            <Button
              name="External wallet"
              size="M"
              onClick={toggleExternalSignerOptionsModal}
              type="primary"
            />
          </div>
        )}
      </div>
    )
  }

  return (
    // <DashboardLayout>
    <div className="Airdrop-wrapper">
      <div className="Airdrop-container">
        {id ? null : <FormTitle title="Airdrop Create" />}
        <div className="Airdrop-options-section">
          {id ? (
            <>
              <FormTitle title="Airdrop details" />
              <div>
                <div className="airdropDataInfo">
                  <span>Chain:</span> {airdropData?.chain?.toUpperCase()}
                </div>
                <div className="airdropDataInfo">
                  <span>Network:</span> {airdropData?.network}
                </div>
                <div className="airdropDataInfo">
                  <span>Token:</span> {airdropData?.tokenName}
                </div>
                <div className="airdropDataInfo">
                  <span>Balance:</span>{' '}
                  {selectedTokenbalance && !isNaN(selectedTokenbalance)
                    ? formatNumber4Decimals(selectedTokenbalance)
                    : '-'}
                </div>
              </div>
            </>
          ) : (
            <>
              <SectionTitle title="1. Choose the chain" />
              <ChainOptions />
              <NetworkOptions />
            </>
          )}
          {renderConnectWalletSection()}
          {!id ? (
            <>
              <SectionTitle title="3. Choose token" />
              <div className="tokenSelectContainer">
                <div className="tokenInfoContainer">
                  <TokenSelect
                    userTokens={userTokens}
                    value={selectedToken?.name || userTokens[0]?.name || ''}
                    handleChange={handleChangeToken}
                  />
                  {selectedTokenbalance && !isNaN(selectedTokenbalance) ? (
                    <span>Balance: {formatNumber4Decimals(selectedTokenbalance)}</span>
                  ) : (
                    ''
                  )}
                </div>
                {chain !== 'multiversx' ? (
                  <Button
                    name="Add token"
                    size="M"
                    onClick={toggleAddTokenModal}
                    type="primary"
                    disabled={!airdropState.initiator}
                  />
                ) : null}
              </div>
            </>
          ) : null}
          <SectionTitle title={`${id ? '2' : '4'}. Add transactions`} />
          <h4 style={{ marginBottom: '1rem', marginTop: '0' }}>Add bulk transactions</h4>
          <div className="create-tx-section">
            <form id="csvForm" className="csvForm">
              <span
                className="downloadCsvButton"
                onClick={downloadCsvTemplate}
                style={{ marginTop: '4px' }}>
                Download CSV template
              </span>
              <div className="dragAndDropContainer">
                <TextField
                  inputProps={{
                    id: 'csvFile',
                    accept: '.csv',
                    ref: csvInputRef,
                    className: 'FileUploadInput',
                    onChange: handleFileChange,
                    onClick: handleInputClick,
                  }}
                  type="file"
                  sx={{
                    width: '100%',
                  }}
                />
                {!csvFileName ? (
                  <span className="helperText">
                    Drag and drop your csv file here or click to select
                  </span>
                ) : (
                  <span className="helperText">{csvInputRef.current?.files?.[0]?.name}</span>
                )}
              </div>
              <div className="mb-4 flex w-full justify-between">
                <Button
                  name="Submit CSV"
                  size="M"
                  onClick={handleFormSubmit}
                  type="primary"
                  disabled={!airdropState.initiator || !csvFileName}
                />
                <Button
                  name="Clear input"
                  size="M"
                  onClick={handleClearCsvClick}
                  type="secondary"
                  disabled={!csvFileName}
                />
              </div>
            </form>
            <span className="divider"></span>
            <h4 style={{ marginBottom: '1rem', marginTop: '0' }}>
              Add single transaction manually
            </h4>
            <Button
              name="Add transaction"
              size="M"
              onClick={toggleCreateModal}
              type="primary"
              disabled={!airdropState.initiator}
            />
          </div>
        </div>
        {airdropData.transactions?.length ? (
          <TransactionsTable
            transactionsData={airdropTransactions || []}
            handleSelectionChange={handleSelectionChange}
            handleTransactionClick={handleTransactionClick}
            toggleExecuteModal={toggleExecuteModal}
            toggleRemoveModal={toggleRemoveModal}
            selectedTransactions={selectedTransactions}
            chain={airdropData?.chain as keyof chainsDataType}
            network={airdropData?.network as networkType}
          />
        ) : null}
      </div>
      <CreateModal
        showCreateModal={showCreateModal}
        toggleCreateModal={toggleCreateModal}
        handleSendTransactions={handleSendTransactions}
        airdrop={airdropData}
      />
      <UpdateModal
        showUpdateModal={showUpdateModal}
        toggleUpdateModal={toggleUpdateModal}
        selectedRow={selectedRow}
        setTransactions={setAirdropTransactions}
        id={id}
      />
      <ConfirmRemoveModal
        showRemoveModal={showRemoveModal}
        toggleRemoveModal={toggleRemoveModal}
        transactionsData={airdropTransactions}
        selectedTransactions={selectedTransactions}
        updateAirdropTransactions={setAirdropTransactions}
        bulkId={id}
        selectedToken={selectedToken as tokenSelectObjectType}
      />
      <ConfirmExecuteModal
        showExecuteModal={showExecuteModal}
        toggleExecuteModal={toggleExecuteModal}
        validTransactions={validTransactions}
        clearSelectedTransactions={() => setSelectedTransactions([])}
        nativeCurrencyBalance={nativeCurrencyBalance}
        chain={airdropData?.chain as keyof chainsDataType}
        network={airdropData?.network as networkType}
        selectedTokenBalance={selectedTokenbalance || 0}
        selectedToken={selectedToken as tokenSelectObjectType}
        initiator={airdropState.initiator}
        isWalletConnected={isWalletConnected}
      />
      <ReviewCsvModal
        showReviewCsvModal={showReviewCsvModal}
        toggleReviewCsvModal={toggleReviewCsvModal}
        data={erroredEntries}
      />
      <SignerOptionsModal
        showSignerOptionsModal={showSignerOptionsModal}
        toggleSignerOptionsModal={toggleSignerOptionsModal}
        showMnemonicModal={() => setShowMnemonicModal(true)}
        showPemModal={() => setShowPemModal(true)}
        showKeystoreModal={() => setShowKeystoreModal(true)}
        showPrivateKeyModal={() => setShowPrivateKeyModal(true)}
        chain={(airdropData?.chain as keyof chainsDataType) || chain?.toLowerCase()}
      />
      <ExternalSignerOptionsModal
        showExternalSignerOptionsModal={showExternalSignerOptionsModal}
        toggleExternalSignerOptionsModal={toggleExternalSignerOptionsModal}
        initiator={airdropState.initiator}
        chain={(airdropData?.chain || chain?.toLowerCase()) as keyof chainsDataType}
      />
      <MnemonicModal
        showMnemonicModal={showMnemonicModal}
        toggleMnemonicModal={toggleMnemonicModal}
      />
      <KeystoreModal
        showKeystoreModal={showKeystoreModal}
        toggleKeystoreModal={toggleKeystoreModal}
      />
      <PemModal showPemModal={showPemModal} togglePemModal={togglePemModal} />
      <PrivateKeyModal
        showPrivateKeyModal={showPrivateKeyModal}
        togglePrivateKeyModal={togglePrivateKeyModal}
      />
      <AddTokenModal
        showAddTokenModal={showAddTokenModal}
        toggleAddTokenModal={toggleAddTokenModal}
        chain={chain as keyof chainsDataType}
        network={network as networkType}
        getUserTokens={handleGetUserTokens}
      />
    </div>
  )
}
